import ButtonGroup from '@components/forms/ButtonGroup'
import Input from '@components/forms/Input'
import { SETTINGS_BUTTON_TITLE_CLASSES } from '@components/modals/SettingsModal'
import Button from '@components/shared/Button'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { useTranslation } from 'next-i18next'
import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import store from 'stores/store'
import {
  DEFAULT_PRIORITY_FEE_LEVEL,
  PRIORITY_FEE_KEY,
  PRIORITY_FEE_LEVELS,
  RPC_PROVIDER_URL_KEY,
  RPC_PROVIDERS,
} from 'utils/constants'

const RPC_URL_CHOICES = RPC_PROVIDERS.map(
  ({ label, url }) => ({ label, value: url }) as const,
)

function isValidCustomUrl(url: string | undefined): url is string {
  return Boolean(url?.trim() && URL.canParse(url))
}

const storeActions = store.getState().actions

const RpcSettings = () => {
  const { t } = useTranslation('settings')
  const [storedRpcEndpointProviderUrl, setStoredRpcEndpointProviderUrl] =
    useLocalStorage(RPC_PROVIDER_URL_KEY)
  const [selectedRpcEndpointProvider, setSelectedRpcEndpointProvider] =
    useState(
      () =>
        RPC_URL_CHOICES.find(
          (provider) => provider.value === storedRpcEndpointProviderUrl,
        )?.label ?? 'Custom',
    )
  const [customRpcEndpointProviderUrl, setCustomRpcEndpointProviderUrl] =
    useState(() => {
      const knownProviderUrl = RPC_URL_CHOICES.find(
        (provider) => provider.value === storedRpcEndpointProviderUrl,
      )
      return knownProviderUrl ? '' : storedRpcEndpointProviderUrl
    })

  // const [storedUseOrderbookFeed, setStoredUseOrderbookFeed] =
  //   useLocalStorageState(USE_ORDERBOOK_FEED_KEY, true)

  const [storedPriorityFeeLevel, setStoredPriorityFeeLevel] =
    useLocalStorage(PRIORITY_FEE_KEY)
  const priorityFee = useMemo(() => {
    return (
      PRIORITY_FEE_LEVELS.find(
        (level) => level.value === storedPriorityFeeLevel,
      ) || DEFAULT_PRIORITY_FEE_LEVEL
    )
  }, [storedPriorityFeeLevel])

  const handleSetRpcEndpointProvider = useCallback(
    (providerLabel: string) => {
      setSelectedRpcEndpointProvider(providerLabel)

      const knownEndpointProvider = RPC_URL_CHOICES.find(
        (provider) => provider.label === providerLabel,
      )
      if (
        knownEndpointProvider ||
        isValidCustomUrl(customRpcEndpointProviderUrl)
      ) {
        const newUrl =
          knownEndpointProvider?.value ?? customRpcEndpointProviderUrl
        setStoredRpcEndpointProviderUrl(newUrl)
        storeActions.updateConnection(newUrl)
      }
    },
    [customRpcEndpointProviderUrl, setStoredRpcEndpointProviderUrl],
  )

  const handlePriorityFee = useCallback(
    (label: string) => {
      const fee = PRIORITY_FEE_LEVELS.find((fee) => fee.label === label)
      if (fee) {
        setStoredPriorityFeeLevel(fee.value)
        // if (wallet) {
        //   storeActions.connectMangoClientWithWallet(wallet)
        // }
      }
    },
    [setStoredPriorityFeeLevel],
  )

  const handleSaveCustomEndpoint = useCallback(() => {
    if (!isValidCustomUrl(customRpcEndpointProviderUrl)) {
      return
    }
    setStoredRpcEndpointProviderUrl(customRpcEndpointProviderUrl)
    storeActions.updateConnection(customRpcEndpointProviderUrl)
  }, [customRpcEndpointProviderUrl, setStoredRpcEndpointProviderUrl])

  return (
    <div className="border-b border-th-bkg-3">
      <div className="flex flex-col border-t border-th-bkg-3 py-4 md:flex-row md:items-center md:justify-between md:px-4">
        <p className={`mb-2 md:mb-0 ${SETTINGS_BUTTON_TITLE_CLASSES}`}>
          {t('rpc-provider')}
        </p>
        <div className="w-full min-w-[330px] md:w-auto">
          <ButtonGroup
            activeValue={selectedRpcEndpointProvider}
            onChange={(v) => handleSetRpcEndpointProvider(v)}
            values={[...RPC_URL_CHOICES.map((val) => val.label), 'Custom']}
            names={[...RPC_URL_CHOICES.map((val) => val.label), t('custom')]}
          />
          {selectedRpcEndpointProvider === 'Custom' ? (
            <div className="mt-2">
              <div className="flex space-x-2">
                <Input
                  type="text"
                  name="url"
                  id="url"
                  className="!h-10"
                  placeholder={t('rpc-url')}
                  value={customRpcEndpointProviderUrl}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setCustomRpcEndpointProviderUrl(e.target.value.trim())
                  }}
                />
                <Button
                  className="whitespace-nowrap"
                  disabled={!isValidCustomUrl(customRpcEndpointProviderUrl)}
                  onClick={handleSaveCustomEndpoint}
                >
                  {t('save')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col border-t border-th-bkg-3 py-4 md:flex-row md:items-center md:justify-between md:px-4">
        <p className={`mb-2 md:mb-0 ${SETTINGS_BUTTON_TITLE_CLASSES}`}>
          {t('priority-fee')}
        </p>
        <div className="min-w-[320px] md:w-auto md:pl-4">
          <ButtonGroup
            activeValue={priorityFee.label}
            onChange={(v) => handlePriorityFee(v)}
            values={PRIORITY_FEE_LEVELS.map((val) => val.label)}
            names={PRIORITY_FEE_LEVELS.map((val) => t(val.label))}
          />
        </div>
      </div>
      {/* <div className="flex items-center justify-between border-t border-th-bkg-3 p-4">
        <Tooltip
          content={t('settings:tooltip-orderbook-bandwidth-saving')}
          maxWidth="25rem"
          placement="top-start"
          delay={100}
        >
          <p
            className={`tooltip-underline mb-2 md:mb-0 ${SETTINGS_BUTTON_TITLE_CLASSES}`}
          >
            {t('settings:orderbook-bandwidth-saving')}
          </p>
        </Tooltip>
        <Switch
          checked={storedUseOrderbookFeed}
          onChange={() => setStoredUseOrderbookFeed(!storedUseOrderbookFeed)}
        />
      </div> */}
    </div>
  )
}

export default RpcSettings
